import { FormProvider, useForm } from 'react-hook-form'
import React, { useCallback, useContext, useState } from 'react'
import { StandardRegisterProps } from '.'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  MergeCartsById,
  MergeCartsByIdMutation,
  MergeCartsByIdMutationVariables,
  ActivationStatus,
  CreateActivation,
  CreateActivationMutationVariables,
  CreateActivationMutation,
} from 'src/graphql/generated/graphql-types'
import { State } from 'src/components/registerBusiness/standardRegister'
import {
  validationSchemaAccount,
  validationSchemaEmail,
} from 'src/components/registerBusiness/standardRegister/validation'
import Error from 'src/components/registerBusiness/standardRegister/Error'
import cookieHandler from '@dustin-web/auth-js/dist/handlers/cookieHandler'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import CustomRedirect from 'src/components/customRedirect'
import AppContext from 'src/appContext'
import { accountCreated } from 'src/util/googleAnalyticsWrapperV2'
import Spinner from '@dustin-web/microsite-components/src/spinner'
import StepperProcess from '@dustin-web/microsite-components/src/stepper-process'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'

const useYupValidationResolver = (state: State) =>
  useCallback(
    async (data: StandardRegisterProps) => {
      try {
        const schema = schemas[State[state]]

        const values = await schema.validate(data, {
          abortEarly: false,
        })

        return {
          values,
          errors: {},
        }
      } catch (errors: any) {
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors: any, currentError: any) => ({
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? 'validation',
                message: currentError.message,
              },
            }),
            {}
          ),
        }
      }
    },
    [state]
  )

const schemas: any = {
  email: validationSchemaEmail,
  account: validationSchemaAccount,
}

type FormProps = {
  defaultValues: StandardRegisterProps
  state: State
  returnUrl: string | null
  onStateChange: (state: State) => void
  children: React.ReactNode
}

const Form: React.FC<FormProps> = ({
  defaultValues,
  state,
  returnUrl,
  onStateChange,
  children,
}) => {
  const { sourcingCompany } = useContext(AppContext)
  const [status, setStatus] = useState<ActivationStatus | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const cookies = cookieHandler()
  const { setCurrentContact } = useAuth()
  const [redirectLink, setRedirectLink] = useState<string | null>(null)
  const { isSmallScreen } = useResponsive()
  const { translate } = useTranslations()

  const methods = useForm<StandardRegisterProps>({
    resolver: useYupValidationResolver(state),
    defaultValues,
  })

  const [mergeCartsById] = useGraphqlMutation<
    MergeCartsByIdMutation,
    MergeCartsByIdMutationVariables
  >(MergeCartsById)

  const [mutation] = useGraphqlMutation<
    CreateActivationMutation,
    CreateActivationMutationVariables
  >(CreateActivation)

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') e.preventDefault()
  }

  const onSubmit = async ({
    acceptAccountTerms,
    email,
    firstName,
    lastName,
    offersByEmail,
    preRegistrationToken,
  }: StandardRegisterProps) => {
    if (state !== State.account) {
      onStateChange(state + 1)
    } else {
      setLoading(true)

      const result: CreateActivationMutation = await mutation({
        variables: {
          firstName,
          lastName,
          accountTermsAccepted: acceptAccountTerms,
          offersByEmail,
          email: email == '' ? defaultValues.email : email,
          preRegistrationToken,
        },
      })

      if (
        result.createActivation?.status === ActivationStatus.Success &&
        result?.createActivation?.activationId
      ) {
        const { cartId, isAuthenticated } = defaultValues

        accountCreated(sourcingCompany)

        if (isAuthenticated) {
          try {
            if (!!cartId) {
              await mergeCartsById({
                variables: {
                  sourceCartId: cartId,
                  targetContactId: result.createActivation.activationId,
                },
              })
            }
          } catch (error) {
            console.error('Failed to merge cart', {
              error,
              contactId: result.createActivation.activationId,
            })
          }

          await setCurrentContact(result.createActivation.activationId)
          setRedirectLink(returnUrl ?? '/')
        } else {
          cookies.setContactId(result.createActivation.activationId)
          onStateChange(State.emailVerification)
        }
      }

      setStatus(result.createActivation?.status ?? null)
      setLoading(false)
    }
  }

  if (redirectLink) {
    return <CustomRedirect url={redirectLink} />
  }

  return (
    <FormProvider {...methods}>
      {loading && <Spinner dustinSpinner />}
      {!isSmallScreen && (
        <StepperProcess
          labels={[
            translate('Register_email'),
            translate('RegisterBuinsess_Account_Information'),
            translate('Register_EmailVerification')
          ]}
          currentIdx={state > 0 ? state - 1 : state}
          bgCompletedClassName={'u-bg-dark-gray'}
          activeStepClassName="u-color-dark-gray u-fw-bold"
        />
      )}
      <div className="u-pt-16 u-pt-lg-40">
        <form onSubmit={methods.handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
          {children}
        </form>
      </div>
      <Error email={methods.getValues('email')} returnUrl={returnUrl} status={status} />
    </FormProvider>
  )
}

export default Form
